import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api/client';
import { toast } from 'react-hot-toast';
import { adminSetRehome } from '../../api/rehome';
import { MPL, MPL_FONT_BODY, MPL_FONT_DISPLAY, PawMark } from '../../styles/mypetlive';

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los roles' },
  { value: 'tenant', label: 'Adoptante' },
  { value: 'landlord', label: 'Protectora' },
  { value: 'store', label: 'Partner · tienda' },
  { value: 'vet', label: 'Partner · veterinaria' },
  { value: 'admin', label: 'Admin' },
];
const ROLE_LABEL: Record<string, string> = { tenant: 'Adoptante', landlord: 'Protectora', store: 'Tienda', vet: 'Veterinaria', admin: 'Admin' };

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', color: MPL.muted, fontWeight: 700 };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 14, color: MPL.ink, borderTop: `1px solid ${MPL.border}` };
const ctrl: React.CSSProperties = { border: `1px solid ${MPL.border}`, borderRadius: 10, padding: '9px 12px', fontFamily: MPL_FONT_BODY, color: MPL.ink, background: '#fff' };

export default function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // La query iba directa contra `q`, así que cada tecla lanzaba una petición.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin/users', { q: debouncedQ, role, page, limit: pageSize }],
    queryFn: async () => {
      const { data } = await api.get('/api/users', { params: { q: debouncedQ, role, page, limit: pageSize } });
      return data as { items: any[]; total: number; page: number; limit: number };
    },
  });
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / (data?.limit || pageSize)));
  const pageItems = data?.items || [];

  const exportCsv = () => {
    const rows = pageItems.map((u: any) => ({ email: u.email, role: u.role, createdAt: u.createdAt }));
    const header = 'email,role,createdAt\n';
    const body = rows.map(r => [r.email, r.role, r.createdAt ? String(r.createdAt) : ''].join(',')).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `users_page_${data?.page || 1}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ fontFamily: MPL_FONT_BODY, color: MPL.ink, background: MPL.bg, minHeight: '100vh', padding: '32px 20px' }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 16 }}>
        <header style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: MPL.teal }}>
          <PawMark size={24} />
          <h1 style={{ fontFamily: MPL_FONT_DISPLAY, fontSize: 24, fontWeight: 800, color: MPL.ink, margin: 0 }}>Usuarios</h1>
          {typeof data?.total === 'number' && <span style={{ fontSize: 13, color: MPL.muted }}>{data.total}</span>}
        </header>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input placeholder="Buscar por email o rol…" value={q} onChange={e => setQ(e.target.value)} style={{ ...ctrl, width: 320, maxWidth: '100%' }} />
          <select value={role} onChange={e => { setRole(e.target.value); setPage(1); }} style={ctrl}>
            {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* Solo baja la página cargada, no los `total` usuarios: decirlo. */}
          <button onClick={exportCsv} disabled={pageItems.length === 0} style={{ ...ctrl, cursor: pageItems.length ? 'pointer' : 'default', opacity: pageItems.length ? 1 : .5, fontWeight: 700, color: MPL.tealDark, background: MPL.teal100, borderColor: MPL.teal100 }}>
            Exportar esta página
          </button>
        </div>

        {/* Antes había un estado de error que nadie rellenaba nunca (setError solo
            se llamaba con null), así que un fallo de red se pintaba abajo como
            "No hay usuarios para esos filtros". */}
        {isError && (
          <div style={{ background: MPL.card, border: `1px solid ${MPL.border}`, borderRadius: 12, padding: 14, display: 'grid', gap: 8, justifyItems: 'start' }}>
            <div style={{ fontWeight: 800 }}>No hemos podido cargar los usuarios.</div>
            <div style={{ color: MPL.muted, fontSize: 14 }}>Puede ser un problema de conexión. No des por hecho que no hay resultados.</div>
            <button onClick={() => refetch()} style={{ ...ctrl, cursor: 'pointer', fontWeight: 800, color: MPL.tealDark }}>Reintentar</button>
          </div>
        )}

        <div style={{ background: MPL.card, border: `1px solid ${MPL.border}`, borderRadius: 16, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: MPL.panel }}>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Rol</th>
                <th style={th}>Creado</th>
                <th style={th} title="Puede publicar animales suyos para cederlos (cesión entre particulares)">Busca casa a animales</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={4} style={{ ...td, color: MPL.muted }}>Cargando…</td></tr>
              ) : isError ? (
                <tr><td colSpan={4} style={{ ...td, color: MPL.muted }}>—</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={4} style={{ ...td, color: MPL.muted }}>No hay usuarios para esos filtros.</td></tr>
              ) : pageItems.map((u: any) => (
                <tr key={u._id}>
                  <td style={td}>{u.email}</td>
                  <td style={td}>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: MPL.tealDark, background: MPL.teal100, borderRadius: 999, padding: '2px 8px' }}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td style={{ ...td, color: MPL.muted, fontSize: 13 }}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ''}</td>
                  <td style={td}>
                    {u.role === 'tenant' ? (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={Boolean(u.rehome?.enabled)}
                          onChange={async e => {
                            const on = e.target.checked;
                            if (!window.confirm(on ? `¿Dejar que ${u.email} publique animales suyos para cederlos?` : `¿Quitarle la opción a ${u.email}? Sus anuncios abiertos se ocultan.`)) return;
                            try { await adminSetRehome(u._id, on); toast.success(on ? 'Activado' : 'Desactivado'); refetch(); } catch { toast.error('No se pudo cambiar'); }
                          }}
                          style={{ width: 18, height: 18, accentColor: MPL.teal }}
                        />
                        {u.rehome?.enabled ? 'Sí' : 'No'}
                      </label>
                    ) : <span style={{ color: MPL.muted, fontSize: 13 }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', alignItems: 'center' }}>
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ ...ctrl, cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}>Anterior</button>
          <span style={{ fontSize: 13, color: MPL.muted }}>Página {data?.page || page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={{ ...ctrl, cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}>Siguiente</button>
        </div>
      </div>
    </div>
  );
}
